import { makeAutoObservable, runInAction } from 'mobx';
import { Entity } from '../../lib/base/viewModels';
import { EntityStreamBuffer } from './EntityStreamBuffer';
import { ListStoreFetchResult } from './ListStore';

export class EntityStream<TEntity extends Entity<TId>, TId = TEntity['id']> {
  private data: Array<TEntity>;
  private buffer: EntityStreamBuffer<TEntity>;
  public loading = false;
  public pointer = 0;

  constructor(
    bufferSize: number,
    fetch: (limit: number, offset: number) => Promise<ListStoreFetchResult<TEntity>>
  ) {
    this.buffer = new EntityStreamBuffer(fetch, bufferSize, Math.ceil(bufferSize / 2 / 5) * 5);
    this.data = new Array<TEntity>();
    makeAutoObservable(this);
  }

  public movePointer(steps: number) {
    this.pointer += steps;
  }

  public setPointer(pointer: number) {
    this.pointer = pointer;
  }

  public async read(limit?: number) {
    limit = limit && limit > 0 ? limit : 1;
    const result = await this.readPage(limit, this.pointer);

    if (result.length != 0) {
      this.pointer += limit;
    }

    return result;
  }

  public async readReverse(limit?: number) {
    limit = limit && limit > 0 ? limit : 1;

    let newPointer = this.pointer - limit * 2;
    newPointer = newPointer < 0 ? 0 : newPointer;

    const result = this.readPage(limit, newPointer);

    this.pointer = newPointer;
    return result;
  }

  public async readPage(limit: number, offset: number) {
    if (limit <= 0) throw new Error('limit must not be zero');
    const overflow = offset + limit - this.data.length;

    if (overflow > 0) {
      this.loading = true;
      try {
        await this.consumeBuffer(overflow);
      } finally {
        runInAction(() => {
          this.loading = this.buffer.pendingRequestCount > 1;
        });
      }
    }
    return this.data.slice(offset, offset + limit);
  }

  private async consumeBuffer(entityCount: number) {
    const newEntities = await this.buffer.consume(entityCount);
    runInAction(() => {
      this.data.push(...newEntities);
    });
  }

  public get length() {
    return this.buffer.streamLength;
  }

  public remove(entities: TEntity[]) {
    this.data = this.data.filter((data) => !entities.some((remove) => data.id === remove.id));
  }

  public changeBufferSize(size: number, threshold = size) {
    this.buffer.changeSize(size, threshold);
  }

  public clear() {
    this.data = [];
    this.buffer.clear();
    this.pointer = 0;
    this.loading = false;
  }

  public updateEntities(entityList: Array<TEntity>) {
    runInAction(() => {
      const updatedEntities = new Map(entityList.map((entity) => [entity.id, entity]));

      this.data = this.data.map((existingEntity) =>
        updatedEntities.has(existingEntity.id)
          ? updatedEntities.get(existingEntity.id)!
          : existingEntity
      );
    });
  }
}
